import React, {useEffect, useState, useRef, useCallback} from 'react';
import api from '../api/axiosInstance';
import ScrollableText from "../components/scrollableText";

const statuses = ['new', 'in progress', 'resolved', 'archived'];

export default function AdminContactMessages() {
    const [messages, setMessages] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedMessageId, setSelectedMessageId] = useState(null);
    const [newNote, setNewNote] = useState('');
    const [status, setStatus] = useState('');
    const notesEndRef = useRef(null);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailMessage, setEmailMessage] = useState('');
    const [emailLoading, setEmailLoading] = useState(false);

    useEffect(() => {
        scrollToBottom();
    }, [selectedMessageId]);


    const fetchMessages = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/contact/contact', {
                params: {page, q: search}
            });
            setMessages(res.data.messages);
            setTotalPages(res.data.totalPages);
        } catch (err) {
            console.error('Failed to fetch messages', err);
        } finally {
            setLoading(false);
        }
    }, [page, search]);

    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    function openMessage(msg) {
        setSelectedMessageId(msg._id);
        setStatus(msg.status || 'new');
        setNewNote('');
    }

    async function updateMessage() {
        if (!selectedMessage) return;
        try {
            const msg = selectedMessage._id;
            const payload = {status};
            if (newNote.trim()) {
                payload.notes = newNote;
            }

            await api.patch(
                `/admin/contact/contact/${selectedMessage._id}`,
                payload
            );
            await fetchMessages();
            await setNewNote('');
            await setSelectedMessageId(msg);
            scrollToBottom();
        } catch (err) {
            console.error('Failed to update message', err);
        }
    }

    async function refreshMessage() {
        if (!selectedMessage) return;
        try {
            const messageId = selectedMessage._id;
            await fetchMessages();
            setSelectedMessageId((messages.find(msg => msg.id === messageId))._id);
        } catch (err) {
            console.error('Failed to update message', err);
        }
    }

    async function sendEmail() {
        const msgId = selectedMessage._id;
        let introduction;
        if (selectedMessage?.firstname) {
            introduction = `Dear ${selectedMessage.firstname},\n`
        } else {
            introduction = `Dear Customer,\n\n`
        }
        if (!emailSubject.trim() || !emailMessage.trim()) return;
        setEmailLoading(true);
        try {
            await api.post('/admin/contact/emailUser', {
                to: selectedMessage.email,
                subject: emailSubject,
                message: `${introduction} ${emailMessage}`,
                orderId: selectedMessage.orderId || null,
                msgId: msgId || null,
            });
            setShowEmailModal(false);
        } catch (err) {
            console.error('Failed to send email', err);
            alert('Failed to send email');
        } finally {
            setEmailLoading(false);
        }
        await fetchMessages();
        await setNewNote('');
        await setSelectedMessageId(msgId);
        scrollToBottom();
    }

    function scrollToBottom() {
        if (notesEndRef.current) {
            notesEndRef.current.scrollIntoView({behavior: 'smooth'});
        }
    }

    const selectedMessage = messages.find(msg => msg._id === selectedMessageId);

    return (
        <div className="p-4 md:max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-4">Customer Messages</h1>

            <div className="mb-4 flex items-center gap-2">
                <input
                    type="text"
                    placeholder="Search messages..."
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                    }}
                    className="border border-gray-300 px-3 py-2 rounded-md w-full md:w-1/2"
                />
            </div>

            {loading ? (
                <p>Loading messages...</p>
            ) : (
                <div className="hide-scrollbar overflow-x-auto">
                    <table className="md:w-full border text-sm">
                        <thead>
                        <tr className="bg-gray-100">
                            <th className="p-2 text-left">Email</th>
                            <th className="p-2 text-left">Subject</th>
                            <th className="p-2 text-left">Order #</th>
                            <th className="p-2 text-left">Status</th>
                            <th className="p-2">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {messages.map((msg) => (
                            <tr key={msg._id} className="border-t even:bg-gray-100">
                                <td className="p-2">{msg.email || '—'}</td>
                                <td className="p-2">{msg.subject}</td>
                                <td className="p-2">{msg.orderId || '—'}</td>
                                <td className="p-2">{msg.status || 'new'}</td>
                                <td className="p-2 text-center">
                                    <button
                                        className="text-blue-600 underline"
                                        onClick={() => openMessage(msg)}
                                    >
                                        View/Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-4 flex justify-between">
                <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                >
                    Prev
                </button>
                <p>
                    Page {page} of {totalPages}
                </p>
                <button
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                >
                    Next
                </button>
            </div>

            {selectedMessageId && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50 p-4">
                    <div
                        className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto shadow-lg relative flex flex-col sm:flex-col md:flex-row">

                        <button
                            onClick={() => setSelectedMessageId(null)}
                            className="absolute top-2 right-2 text-gray-500 hover:text-black z-10"
                        >
                            &times;
                        </button>

                        <div className="md:w-1/2 p-6 flex flex-col">
                            <h2 className="text-lg font-semibold mb-2">User: {selectedMessage.email}</h2>
                            <h2 className="text-lg font-semibold mb-2">{selectedMessage.subject}</h2>
                            <p className="mb-2 text-sm text-textcolor">
                                <b>Order Number:</b> {selectedMessage.orderId || '—'}
                            </p>
                            <p className="mb-4 text-sm text-gray-600"><b>User Message:</b> {selectedMessage.message}</p>

                            <label className="block mb-2 text-sm font-medium">Status</label>
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                className="mb-4 w-full border rounded px-3 py-2"
                            >
                                {statuses.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                            <label className="block mb-2 text-sm font-medium">Previous Notes</label>
                            <ScrollableText text={selectedMessage.notes || []}/>
                        </div>

                        <div className="md:w-1/2 p-6 flex flex-col justify-start">
                            <label className="block mb-2 text-sm font-medium">Add New Note</label>
                            <textarea
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                rows={5}
                                className="w-full border rounded px-3 py-2 mb-4"
                            />

                            <button
                                onClick={updateMessage}
                                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mb-2"
                            >
                                Save Changes
                            </button>
                            <button
                                onClick={refreshMessage}
                                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 mb-2"
                            >
                                Refresh
                            </button>
                            <button
                                onClick={() => {
                                    setEmailSubject(`Re: ${selectedMessage.subject}`);
                                    setEmailMessage('');
                                    setShowEmailModal(true);
                                }}
                                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                            >
                                Send Email
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showEmailModal && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-[60]">
                    <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg relative flex flex-col">
                        <button
                            onClick={() => setShowEmailModal(false)}
                            className="absolute top-2 right-2 text-gray-500 hover:text-black"
                        >
                            &times;
                        </button>

                        <h2 className="text-lg font-semibold mb-2">Send Email to {selectedMessage?.email}</h2>

                        <label className="block mb-2 text-sm font-medium">Subject</label>
                        {selectedMessage?.orderId ? (
                            <label className="mb-4 w-full rounded px-3 py-2">Regarding order
                                ${selectedMessage.orderId}</label>
                        ) : (
                            <input
                                type="text"
                                value={emailSubject}
                                onChange={(e) => setEmailSubject(e.target.value)}
                                className="mb-4 w-full border rounded px-3 py-2"
                            />
                        )}

                        <label className="block mb-2 text-sm font-medium">Message</label>
                        {selectedMessage?.firstname && (
                            <label>Dear {selectedMessage?.firstname},</label>
                        )}
                        {!selectedMessage?.firstname && (
                            <label>Dear Customer,</label>
                        )}
                        <textarea
                            value={emailMessage}
                            onChange={(e) => setEmailMessage(e.target.value)}
                            rows={5}
                            className="w-full border rounded px-3 py-2"
                        />
                        Thanks!
                        <div className="mb-4"/>
                        <button
                            onClick={sendEmail}
                            disabled={emailLoading}
                            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                        >
                            {emailLoading ? 'Sending...' : 'Send Email'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
